import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ShieldAlert, AlertTriangle, AlertCircle, Info, CheckCircle2,
  Filter, Download, FileBarChart2, TrendingUp, X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api-client";
import { StatCard } from "@/components/common/StatCard";
import { RiskHeatMap } from "@/components/common/RiskHeatMap";
import type { HeatMapCell, CellId } from "@/components/common/RiskHeatMap";
import { SelectRoot, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/Select";
import { DateRangePicker } from "@/components/ui/DatePicker";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/Button";
import { DataTable } from "@/components/common/DataTable";
import type { ColumnDef } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { toast } from "@/components/ui/Toast";
import { useTranslation } from "@/hooks/useTranslation";
import { toRiskScore } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import type { RiskLevel } from "@/types";

// ─────────────────────────────────────────────
// RiskReport (merged)
//
// Screen — /risks
// Combines former /risks (RisksOverview) and /reports (Reports) into
// a single "Risk Report" page.
//
// Layout:
//   1. Page header + filter bar (entity filter + date range)
//   2. Severity KPI cards (Critical / High / Medium / Low)
//   3. Portfolio KPI cards (Total / Scored / Avg score)
//   4. Risk Distribution bars + Top Risks side-by-side
//   5. Heat map + cell detail panel
//   6. All Assessments table with per-row PDF export
//
// Design: §8.5 Risks Overview + §8.6 Reports
// Fixes: ITEM-004, ITEM-010, ITEM-020, ITEM-029
// ─────────────────────────────────────────────

// ── Types ─────────────────────────────────────────────────────────────────────

interface RiskSummary {
  bySeverity: {
    CRITICAL: number;
    HIGH: number;
    MEDIUM: number;
    LOW: number;
    VERY_HIGH: number;
  };
  heatMapCells: HeatMapCell[];
  totalRisks: number;
}

interface EntityOption {
  id: string;
  name: string;
}

interface RisksResponse {
  data: RiskSummary;
}

interface CellDetailRow {
  entityId: string;
  entityName: string;
  entityType: string;
  riskLevel: RiskLevel;
  score: number;
}

interface CellDetailResponse {
  data: CellDetailRow[];
}

interface PortfolioSummary {
  generatedAt: string;
  organizationId: string;
  summary: {
    totalAssessments: number;
    scoredAssessments: number;
    averageRiskScore: number | null;
    averageRiskLevel: RiskLevel | null;
  };
  riskDistribution: Record<string, number>;
  topRisks: TopRiskRow[];
}

interface TopRiskRow {
  assessmentId: string;
  entityId: string;
  entityName: string;
  overallScore: number;
  overallLevel: RiskLevel;
  riskMatrixCell: string;
}

interface SearchRow {
  assessmentId: string;
  entityId: string;
  entityName: string;
  entityType: string;
  status: string;
  schemaVersionId: string;
  latestScore: {
    overallScore: number;
    overallLevel: RiskLevel;
    computedAt: string;
  } | null;
  updatedAt: string;
}

interface SearchResponse {
  data: SearchRow[];
  meta: { total: number; page: number; pageSize: number; totalPages: number };
}

// ── Risk display helpers ───────────────────────────────────────────────────────

const RISK_LEVEL_COLORS: Record<RiskLevel, string> = {
  LOW: "text-success",
  MEDIUM: "text-warning",
  HIGH: "text-k-orange",
  VERY_HIGH: "text-k-purple",
  CRITICAL: "text-danger",
};

const RISK_LEVEL_LABEL_KEYS: Record<string, string> = {
  LOW: "risk.low",
  MEDIUM: "risk.medium",
  HIGH: "risk.high",
  VERY_HIGH: "risk.veryHigh",
  CRITICAL: "risk.critical",
};

function RiskBadge({ level }: { level: RiskLevel }) {
  const { t } = useTranslation();
  return (
    <span className={`font-medium text-caption ${RISK_LEVEL_COLORS[level] ?? ""}`}>
      {t(RISK_LEVEL_LABEL_KEYS[level] ?? level)}
    </span>
  );
}

const RISK_DIST_COLORS: Record<string, string> = {
  LOW: "bg-success",
  MEDIUM: "bg-warning",
  HIGH: "bg-k-orange",
  VERY_HIGH: "bg-k-purple",
  CRITICAL: "bg-danger",
};

function RiskDistributionBar({ distribution }: { distribution: Record<string, number> }) {
  const { t } = useTranslation();
  const levels = ["LOW", "MEDIUM", "HIGH", "VERY_HIGH", "CRITICAL"];
  const total = levels.reduce((sum, l) => sum + (distribution[l] ?? 0), 0);
  if (total === 0) return <p className="text-body text-text-muted">{t("reports.noScoredAssessments")}</p>;

  return (
    <div className="flex flex-col gap-3">
      {levels.map((level) => {
        const count = distribution[level] ?? 0;
        const pct = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={level} className="flex items-center gap-3">
            <span className="w-24 flex-shrink-0 text-caption text-text-muted">
              {t(RISK_LEVEL_LABEL_KEYS[level] ?? level)}
            </span>
            <div className="flex-1">
              <div className="h-2 overflow-hidden rounded-full bg-hover">
                <div
                  className={`h-full rounded-full transition-all ${RISK_DIST_COLORS[level] ?? "bg-accent"}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
            <span className="w-6 flex-shrink-0 text-right font-mono text-caption text-text-secondary">
              {count}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function RisksOverview() {
  const { t, language } = useTranslation();

  // Filter state
  const [entityId, setEntityId] = useState<string>("");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  // Heat map state
  const [selectedCell, setSelectedCell] = useState<CellId | null>(null);

  // Assessments table state
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState<string | null>(null);

  // Build optional date params (sent as ISO strings; backend ignores them until ITEM-029 backend lands)
  const fromParam = dateRange?.from
    ? new Date(dateRange.from.setHours(0, 0, 0, 0)).toISOString()
    : undefined;
  const toParam = dateRange?.to
    ? new Date(dateRange.to.setHours(23, 59, 59, 999)).toISOString()
    : undefined;

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: entitiesData } = useQuery<{ data: EntityOption[] }>({
    queryKey: ["entities-risk-options"],
    queryFn: () => api.get("/entities?pageSize=100&status=ACTIVE"),
  });

  const { data: risksData, isLoading: risksLoading } = useQuery<RisksResponse>({
    queryKey: ["risks-summary", entityId, fromParam, toParam],
    queryFn: () => {
      const params = new URLSearchParams();
      if (entityId) params.set("entityId", entityId);
      if (fromParam) params.set("from", fromParam);
      if (toParam) params.set("to", toParam);
      return api.get(`/risks/summary?${params.toString()}`);
    },
  });

  const { data: cellData } = useQuery<CellDetailResponse>({
    queryKey: ["risks-cell", selectedCell, entityId, fromParam, toParam],
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedCell) params.set("cell", selectedCell);
      if (entityId) params.set("entityId", entityId);
      if (fromParam) params.set("from", fromParam);
      if (toParam) params.set("to", toParam);
      return api.get(`/risks/by-cell?${params.toString()}`);
    },
    enabled: Boolean(selectedCell),
  });

  const { data: portfolioData, isLoading: portfolioLoading } = useQuery<{ data: PortfolioSummary }>({
    queryKey: ["reports-portfolio", entityId, fromParam, toParam],
    queryFn: () => {
      const params = new URLSearchParams();
      if (entityId) params.set("entityId", entityId);
      if (fromParam) params.set("from", fromParam);
      if (toParam) params.set("to", toParam);
      return api.get(`/reports/portfolio?${params.toString()}`);
    },
  });

  const { data: searchData, isLoading: searchLoading } = useQuery<SearchResponse>({
    queryKey: ["reports-search", page, entityId, fromParam, toParam],
    queryFn: () =>
      api.post("/reports/portfolio/search", {
        page,
        pageSize: 20,
        ...(entityId ? { entityId } : {}),
        ...(fromParam ? { from: fromParam } : {}),
        ...(toParam ? { to: toParam } : {}),
      }),
  });

  // ── Derived data ───────────────────────────────────────────────────────────

  const entities = entitiesData?.data ?? [];
  const summary = risksData?.data;
  const cells = summary?.heatMapCells ?? [];
  const bySeverity = summary?.bySeverity ?? { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, VERY_HIGH: 0 };
  const portfolio = portfolioData?.data;
  const rows = searchData?.data ?? [];
  const meta = searchData?.meta;

  // ── PDF download ───────────────────────────────────────────────────────────

  async function handleDownloadPdf(assessmentId: string, entityName: string) {
    setDownloading(assessmentId);
    try {
      const res = await fetch(`/api/v1/reports/assessments/${assessmentId}/export`, {
        credentials: "include",
      });
      if (!res.ok) {
        let errMsg = t("errors.exportFailed");
        try {
          const body = await res.json() as { error?: { message?: string } };
          if (body?.error?.message) errMsg = body.error.message;
        } catch { /* ignore */ }
        throw new Error(errMsg);
      }
      const ct = res.headers.get("Content-Type") ?? "";
      if (!ct.includes("application/pdf")) {
        throw new Error("PDF generation failed. The server did not return a valid PDF.");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report-${entityName.replace(/\s+/g, "-").toLowerCase()}-${assessmentId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("errors.exportFailed"));
    } finally {
      setDownloading(null);
    }
  }

  // ── Clear date filter ──────────────────────────────────────────────────────

  function clearDateRange() {
    setDateRange(undefined);
  }

  // ── Assessments table columns ──────────────────────────────────────────────

  const columns: ColumnDef<SearchRow>[] = [
    {
      key: "entity",
      header: t("reports.entity"),
      cell: (row) => (
        <span className="font-medium text-text-primary">{row.entityName}</span>
      ),
    },
    {
      key: "entityType",
      header: t("reports.type"),
      cell: (row) => (
        <span className="text-caption text-text-muted">{t(`entity.types.${row.entityType}`)}</span>
      ),
    },
    {
      key: "status",
      header: t("entity.status"),
      cell: (row) => <StatusBadge type="assessment" status={row.status as any} />,
    },
    {
      key: "score",
      header: t("reports.riskScore"),
      cell: (row) =>
        row.latestScore ? (
          <div className="flex items-center gap-2">
            <span className="font-mono font-medium text-text-primary">
              {toRiskScore(row.latestScore.overallScore)}
            </span>
            <RiskBadge level={row.latestScore.overallLevel} />
          </div>
        ) : (
          <span className="text-text-muted">{t("reports.notScored")}</span>
        ),
    },
    {
      key: "generatedAt",
      header: t("reports.lastScored"),
      sortable: true,
      sortAccessor: (row) =>
        row.latestScore ? new Date(row.latestScore.computedAt) : null,
      cell: (row) =>
        row.latestScore ? (
          <span className="text-text-secondary">
            {formatDate(row.latestScore.computedAt, language)}
          </span>
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: "download",
      header: "",
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          disabled={downloading === row.assessmentId}
          isLoading={downloading === row.assessmentId}
          onClick={(e) => {
            e.stopPropagation();
            handleDownloadPdf(row.assessmentId, row.entityName);
          }}
        >
          <Download className="mr-1.5 h-3.5 w-3.5" />
          {t("reports.pdf")}
        </Button>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Page header + filter bar */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[24px] font-semibold text-text-primary">{t("nav.riskReport")}</h1>
          <p className="mt-1 text-body text-text-muted">
            {t("risks.subtitle")}
          </p>
        </div>

        {/* Filter bar: entity filter + date range */}
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-text-muted flex-shrink-0" />

          {/* Entity filter */}
          <div className="w-48">
            <SelectRoot
              value={entityId || "ALL"}
              onValueChange={(v) => setEntityId(v === "ALL" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t("risks.allEntities")}</SelectItem>
                {entities.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          </div>

          {/* Date range filter (ITEM-029) */}
          <div className="w-64">
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              placeholder={t("risks.allTime")}
            />
          </div>

          {/* Clear date range */}
          {dateRange && (
            <Button variant="ghost" size="sm" onClick={clearDateRange}>
              <X className="h-3.5 w-3.5 mr-1" />
              {t("common.clear")}
            </Button>
          )}
        </div>
      </div>

      {/* Severity KPI cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          icon={ShieldAlert}
          label={t("risks.criticalRisks")}
          value={risksLoading ? "-" : bySeverity.CRITICAL}
        />
        <StatCard
          icon={AlertTriangle}
          label={t("risks.highRisks")}
          value={risksLoading ? "-" : (bySeverity.HIGH + bySeverity.VERY_HIGH)}
        />
        <StatCard
          icon={AlertCircle}
          label={t("risks.mediumRisks")}
          value={risksLoading ? "-" : bySeverity.MEDIUM}
        />
        <StatCard
          icon={CheckCircle2}
          label={t("risks.lowRisks")}
          value={risksLoading ? "-" : bySeverity.LOW}
        />
      </div>

      {/* Portfolio KPI cards */}
      {portfolioLoading ? (
        <div className="grid grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-card bg-hover" />
          ))}
        </div>
      ) : portfolio ? (
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            label={t("reports.totalAssessments")}
            value={portfolio.summary.totalAssessments}
            icon={FileBarChart2}
          />
          <StatCard
            label={t("reports.scoredAssessments")}
            value={portfolio.summary.scoredAssessments}
            icon={CheckCircle2}
          />
          <StatCard
            label={t("reports.averageRiskScore")}
            value={
              portfolio.summary.averageRiskScore !== null
                ? portfolio.summary.averageRiskScore.toFixed(1)
                : "-"
            }
            icon={TrendingUp}
          />
        </div>
      ) : null}

      {/* Risk distribution + top risks */}
      {portfolio && portfolio.summary.scoredAssessments > 0 && (
        <div className="grid grid-cols-2 gap-6">
          {/* Risk distribution bars */}
          <div className="rounded-card border border-border-default bg-card p-6">
            <h2 className="mb-4 text-[18px] font-semibold text-text-primary">
              {t("reports.riskDistribution")}
            </h2>
            <RiskDistributionBar distribution={portfolio.riskDistribution} />
          </div>

          {/* Top risks */}
          <div className="rounded-card border border-border-default bg-card p-6">
            <h2 className="mb-4 text-[18px] font-semibold text-text-primary">
              {t("reports.topRisks")}
            </h2>
            {portfolio.topRisks.length > 0 ? (
              <div className="flex flex-col gap-3">
                {portfolio.topRisks.slice(0, 6).map((r) => (
                  <div
                    key={r.assessmentId}
                    className="flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <ShieldAlert className="h-4 w-4 flex-shrink-0 text-danger" />
                      <span className="truncate text-caption text-text-primary">
                        {r.entityName}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="font-mono text-caption text-text-secondary">
                        {r.overallScore.toFixed(1)}
                      </span>
                      <RiskBadge level={r.overallLevel} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-body text-text-muted">{t("reports.noHighRisk")}</p>
            )}
          </div>
        </div>
      )}

      {/* Heat map + cell detail panel */}
      <div className="grid grid-cols-2 gap-6">
        {/* 5×5 Heat Map */}
        <div className="rounded-card border border-border-default bg-card p-6">
          <h2 className="mb-4 text-[18px] font-semibold text-text-primary">{t("risks.riskHeatMap")}</h2>
          <RiskHeatMap
            cells={cells}
            onCellClick={(cellId) => setSelectedCell(cellId === selectedCell ? null : cellId)}
            selectedCell={selectedCell}
          />
        </div>

        {/* Cell detail or Phase 2 placeholder */}
        <div className="rounded-card border border-border-default bg-card p-6">
          {selectedCell ? (
            <>
              <h2 className="mb-4 text-[18px] font-semibold text-text-primary">
                Cell {selectedCell}
              </h2>
              {cellData?.data && cellData.data.length > 0 ? (
                <div className="flex flex-col gap-2">
                  {cellData.data.map((row) => (
                    <Link
                      key={row.entityId}
                      to={`/apps/${row.entityId}`}
                      className="flex items-center justify-between rounded-control border border-border-subtle bg-hover px-3 py-2 hover:brightness-95 transition-all cursor-pointer"
                    >
                      <span className="text-body text-text-primary">{row.entityName}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-caption text-accent-light">{row.score.toFixed(1)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-body text-text-muted">{t("risks.noRisksInCell")}</p>
              )}
            </>
          ) : (
            <>
              <div className="mb-4 flex items-center gap-2">
                <Info className="h-4 w-4 text-text-muted" />
                <h2 className="text-[18px] font-semibold text-text-primary">{t("risks.threatFindings")}</h2>
              </div>
              <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-hover">
                  <ShieldAlert className="h-6 w-6 text-text-muted opacity-50" />
                </div>
                <p className="text-[15px] font-medium text-text-secondary">
                  {t("risks.threatFindingsPhase2")}
                </p>
                <p className="max-w-xs text-caption text-text-muted">
                  {t("risks.threatFindingsPhase2Desc")}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* All Assessments table with per-row PDF export */}
      <div>
        <h2 className="mb-3 text-[18px] font-semibold text-text-primary">
          {t("reports.allAssessments")}
        </h2>
        <DataTable
          columns={columns}
          data={rows}
          isLoading={searchLoading}
          emptyState={{
            icon: FileBarChart2,
            title: t("reports.noAssessmentsYet"),
            description: t("reports.completeAssessment"),
          }}
          page={meta?.page}
          totalPages={meta?.totalPages}
          totalItems={meta?.total}
          pageSize={meta?.pageSize}
          onPageChange={setPage}
        />
      </div>
    </>
  );
}
